import React, { useState } from "react";
import { useSelector } from "react-redux";
import { IconButton, Button, Typography, Card, Input } from "@material-tailwind/react";
import { Search, Download, EmptyPage, PlusCircle } from "iconoir-react";
import { useDispatch } from "react-redux";
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import Loader from "../../../../components/Loader";
import { RootState } from "../../../../redux";
import {
    uploadDocumentRequest,
    updateVehicleRequest,
    Vehicle,
} from "../../../../redux/vehicles/actions";
import AppImages from "../../../../assets/AppImages";
import { DialogUploadDoc } from "./DialogUploadDoc";
import { secureDownload } from "../../../../redux/api";
import { getFileTypes } from "../../../../utils/formatViews";
import { DialogFilePreview } from "../../../../components/DialogFilePreview";

const TABLE_HEAD = [
    { n: "Document Name", className: "w-1/2" },
    { n: "Filetype", className: "" },
    { n: "Document Type", className: "" },
    { n: "", className: "w-[120px]" },
];

export function DashboardVehicleInventoryTabDocs() {
    const dispatch = useDispatch();
    const { vehicle } = useSelector((state: RootState) => state.vehicles);
    const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
    const [previewUrl, setPreviewUrl] = useState<string>("");

    return !vehicle ? (
        <Loader />
    ) : (
        <React.Fragment>
            {uploadDialogOpen && (
                <DialogUploadDoc
                    isOpen={uploadDialogOpen}
                    setIsOpen={setUploadDialogOpen}
                    onSubmit={(files: FileList, documentType: string) => {
                        setUploadDialogOpen(false);
                        dispatch(
                            uploadDocumentRequest({
                                vehicle_id: vehicle?.id || 0,
                                files: files,
                                type: documentType,
                                filenames: Array.from(files).map((file) => file.name),
                            })
                        );
                    }}
                />
            )}

            {previewDialogOpen && (
                <DialogFilePreview
                    isOpen={previewDialogOpen}
                    setIsOpen={setPreviewDialogOpen}
                    url={previewUrl}
                />
            )}

            <Card className="p-5">
                <Card.Header className="w-full m-0 items-start rounded-none flex flex-wrap gap-4 justify-between p-4">
                    <div>
                        <Typography type="lead" color="default" className="font-semibold mb-1">
                            Titles/Documents
                        </Typography>
                        <Typography type="small" className="text-foreground block">
                            All documents regarding this vehicle.
                        </Typography>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center w-full shrink-0 gap-3 md:w-max">
                        <Input placeholder="Search here..." className="w-full sm:w-72">
                            <Input.Icon>
                                <Search className="w-full h-full" />
                            </Input.Icon>
                        </Input>
                        <div className="flex gap-3 sm:w-max w-full">
                            <Button
                                color="primary"
                                className="flex items-center gap-2 w-max"
                                onClick={() => setUploadDialogOpen(true)}
                            >
                                Upload document
                                <PlusCircle className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="mt-4 p-0 rounded-none max-h-[calc(100vh-340px)] overflow-y-auto scrollbar-hide">
                    <table className="w-full min-w-max table-auto">
                        <thead>
                            <tr>
                                {TABLE_HEAD.map((el, idx: number) => (
                                    <th
                                        key={idx}
                                        className={`sticky top-0 bg-white z-10 border-b text-start border-surface p-4 ${el.className}`}
                                    >
                                        <Typography
                                            type="small"
                                            color="default"
                                            className="block font-medium"
                                        >
                                            {el.n}
                                        </Typography>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {vehicle.documents?.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="p-4 border-b border-surface text-center"
                                    >
                                        <Typography type="small" className="block text-foreground">
                                            No documents found
                                        </Typography>
                                    </td>
                                </tr>
                            ) : (
                                vehicle.documents?.map((el, idx: number) => (
                                    <tr key={idx} className="h-20">
                                        <td className="p-4 border-b border-surface">
                                            <Typography color="primary" className="block font-bold">
                                                {el.filename}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface">
                                            <Typography
                                                type="small"
                                                className="block text-foreground"
                                            >
                                                {getFileTypes(el.filename)}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface">
                                            <Typography
                                                type="small"
                                                className="block text-foreground"
                                            >
                                                {el.type_display}
                                            </Typography>
                                        </td>
                                        <td className="py-4 border-b border-surface">
                                            <IconButton
                                                variant="ghost"
                                                onClick={() => {
                                                    withReactContent(Swal)
                                                        .fire({
                                                            title: "Are you sure?",
                                                            text: "You won't be able to revert this!",
                                                            icon: "warning",
                                                            showCancelButton: true,
                                                            confirmButtonColor: "#3085d6",
                                                            cancelButtonColor: "#d33",
                                                            confirmButtonText: "Yes, delete it!",
                                                        })
                                                        .then((result) => {
                                                            if (result.isConfirmed) {
                                                                dispatch(
                                                                    updateVehicleRequest({
                                                                        vehicle: {
                                                                            id: vehicle.id,
                                                                            removed_documents: [
                                                                                el.id,
                                                                            ],
                                                                        } as unknown as Vehicle,
                                                                        update_fields: [
                                                                            "id",
                                                                            "removed_documents",
                                                                        ],
                                                                    })
                                                                );
                                                            }
                                                        })
                                                        .catch((error: any) => {
                                                            console.log("error", error);
                                                        });
                                                }}
                                            >
                                                <AppImages.iconTrash className="text-error w-5 h-5" />
                                            </IconButton>
                                            <IconButton
                                                variant="ghost"
                                                onClick={() => {
                                                    setPreviewUrl(el.file);
                                                    setPreviewDialogOpen(true);
                                                }}
                                            >
                                                <EmptyPage className="w-5 h-5" />
                                            </IconButton>
                                            <IconButton
                                                variant="ghost"
                                                onClick={() => {
                                                    secureDownload(el.file, el.filename);
                                                }}
                                            >
                                                <Download className="w-6 h-6" />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </Card.Body>
            </Card>
        </React.Fragment>
    );
}
